import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, map, tap } from 'rxjs';

import type { RolResumido, SesionAuthMe } from '../models/usuario-equipo.model';
import type { OrganizacionPerfil } from '../models/organizacion.model';
import { normalizarOrganizacionPerfilDesdeApi } from './organizaciones.service';
import {
  AUTH_STORAGE_KEYS,
  escribirItemEnBucket,
  iniciarBucketSesion,
  leerItemSesionActiva,
  limpiarSesionEnAmbosBuckets,
  resolverBucketSesionActiva,
  type AuthSessionBucket,
} from '../utils/auth-session-storage.util';
import { jwtEstaVigente } from '../utils/jwt-session.util';
import {
  PERMISO_LISTAR_PAGOS,
  PERMISO_LISTAR_USUARIOS,
  tieneAlgunoPermiso,
} from '../utils/permisos-acceso.util';
import { esNombreRolVendedor } from '../utils/vendedor-rol.util';
import { API_BASE_URL } from '../tokens/api-base-url';

const STORAGE_KEY = AUTH_STORAGE_KEYS.token;
const STORAGE_PERMISOS_KEY = AUTH_STORAGE_KEYS.permisos;
const STORAGE_NOMBRE_KEY = AUTH_STORAGE_KEYS.nombre;
const STORAGE_ROL_ID_KEY = AUTH_STORAGE_KEYS.rolId;
const STORAGE_ROL_NOMBRE_KEY = AUTH_STORAGE_KEYS.rolNombre;
const STORAGE_USER_ID_KEY = AUTH_STORAGE_KEYS.userId;
const STORAGE_ORGANIZACION_KEY = AUTH_STORAGE_KEYS.organizacion;

/** Convención del API / `backend.md`: rol administrador con catálogo completo. */
export const ROL_ID_ADMINISTRADOR = 1;

export interface LoginUsuario {
  id: number;
  organizacion_id?: number;
  rol_id: number;
  nombre_completo: string;
}

/** Permisos efectivos devueltos por `POST /auth/login` (misma forma útil que filas del catálogo). */
export interface PermisoSesion {
  id: number;
  modulo: string;
  accion: string;
  descripcion?: string | null;
}

export interface LoginResponse {
  token: string;
  usuario: LoginUsuario;
  /** Lista opcional pero esperada en instalaciones actuales; alimenta el menú y checks en cliente. */
  permisos?: PermisoSesion[];
  /** Perfil del negocio devuelto por `POST /auth/login` (persistido en storage). */
  organizacion?: OrganizacionPerfil | null;
}

export interface SetupRequiredResponse {
  setupRequired: boolean;
  usuariosQuePuedenIniciarSesion?: number;
}

export interface InitialSetupBody {
  email: string;
  password: string;
  nombre_completo: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = inject(API_BASE_URL);

  private readonly tokenWritable = signal<string | null>(this.readTokenFromStorage());
  private readonly nombreWritable = signal<string | null>(this.readNombreFromStorage());
  private readonly rolIdWritable = signal<number | null>(this.readRolIdFromStorage());
  private readonly rolNombreWritable = signal<string | null>(this.readRolNombreFromStorage());
  private readonly permisosWritable = signal<PermisoSesion[]>(this.readPermisosFromStorage());
  private readonly userIdWritable = signal<number | null>(this.readUserIdFromStorage());

  constructor() {
    resolverBucketSesionActiva();
    this.tokenWritable.set(this.readTokenFromStorage());
    this.nombreWritable.set(this.readNombreFromStorage());
    this.rolIdWritable.set(this.readRolIdFromStorage());
    this.rolNombreWritable.set(this.readRolNombreFromStorage());
    this.permisosWritable.set(this.readPermisosFromStorage());
    this.userIdWritable.set(this.readUserIdFromStorage());
  }

  readonly token = computed(() => this.tokenWritable());
  readonly displayName = computed(() => this.nombreWritable() ?? 'Usuario');
  /** `rol_id` persistido del login (fallback JWT si falta storage). */
  readonly rolId = computed(() => this.rolAlmacenadoOJwt());
  /** Nombre del rol (p. ej. «Vendedor») desde login o `GET /auth/me`. */
  readonly rolNombre = computed(() => this.rolNombreWritable());
  /** `id` del usuario autenticado (persistido junto al JWT en el login). */
  readonly userId = computed(() => this.userIdWritable());

  /** Permisos del último login (persistidos junto al JWT). */
  readonly permisos = computed(() => this.permisosWritable());

  /** Relee storage (p. ej. tras recarga o cambio en otra pestaña). */
  syncFromStorage(): void {
    resolverBucketSesionActiva();
    this.tokenWritable.set(this.readTokenFromStorage());
    this.nombreWritable.set(this.readNombreFromStorage());
    this.rolIdWritable.set(this.readRolIdFromStorage());
    this.rolNombreWritable.set(this.readRolNombreFromStorage());
    this.permisosWritable.set(this.readPermisosFromStorage());
    this.userIdWritable.set(this.readUserIdFromStorage());
  }

  isAuthenticated(): boolean {
    const token = this.tokenWritable();
    return !!token && jwtEstaVigente(token);
  }

  /** Token actual para el interceptor (sin exponer el signal si no hace falta). */
  getToken(): string | null {
    return this.tokenWritable();
  }

  logout(): void {
    limpiarSesionEnAmbosBuckets();
    this.tokenWritable.set(null);
    this.nombreWritable.set(null);
    this.rolIdWritable.set(null);
    this.rolNombreWritable.set(null);
    this.permisosWritable.set([]);
    this.userIdWritable.set(null);
  }

  /** ¿Tiene exactamente este par modulo+acción en la sesión? */
  tienePermiso(modulo: string, accion: string): boolean {
    const M = modulo.trim().toUpperCase();
    const A = accion.trim().toUpperCase();
    return this.permisosWritable().some(
      (p) => p.modulo.trim().toUpperCase() === M && p.accion.trim().toUpperCase() === A,
    );
  }

  /** ¿Tiene al menos uno de los pares modulo+acción? */
  tieneAlgunoDe(permisos: readonly { modulo: string; accion: string }[]): boolean {
    if (!permisos.length) return true;
    return permisos.some((p) => this.tienePermiso(p.modulo, p.accion));
  }

  /**
   * Visibilidad de ítems del menú lateral según permisos del login.
   * Sin `permisoAnyOf`: solo autenticado (salvo flags de administrador en nav-visible).
   * Con lista: basta uno (OR). Sin permisos cargados, los ítems restringidos no se muestran.
   */
  itemNavVisible = (permisoAnyOf?: readonly { modulo: string; accion: string }[]): boolean => {
    if (!this.isAuthenticated()) return false;
    if (!permisoAnyOf?.length) return true;
    if (this.permisosWritable().length === 0) return false;
    return this.tieneAlgunoDe(permisoAnyOf);
  };

  /**
   * Rol administrador del negocio (`rol_id === 1`, semilla documentada en `backend.md`).
   * Sin `rol_id` en storage (sesión vieja) se intenta leer del JWT.
   */
  esRolAdministrador = (): boolean => {
    return this.rolAlmacenadoOJwt() === ROL_ID_ADMINISTRADOR;
  };

  /** ¿Puede listar usuarios (`GET /usuarios`)? */
  puedeListarUsuarios(): boolean {
    return tieneAlgunoPermiso((m, a) => this.tienePermiso(m, a), PERMISO_LISTAR_USUARIOS);
  }

  /** ¿Puede consultar listado de pagos / abonos? */
  puedeListarPagos(): boolean {
    return tieneAlgunoPermiso((m, a) => this.tienePermiso(m, a), PERMISO_LISTAR_PAGOS);
  }

  /**
   * Alta/edición de cajas físicas y asignación de empleados.
   * `CAJA:GESTIONAR` cubre turnos y movimientos; el catálogo de cajas es solo administrador.
   */
  puedeAdministrarCatalogoCajas(): boolean {
    return this.esRolAdministrador();
  }

  /**
   * Listado global de créditos (plan de cuotas, etc.): admin y gerencia.
   * Vendedores de campo solo ven su cartera / colocación propia.
   */
  puedeVerCatalogoCompletoCreditos(): boolean {
    return this.esRolAdministrador() || this.tienePermiso('REPORTES', 'GESTIONAR');
  }

  /** Rol de campo «Vendedor» (nombre en sesión o comparación con catálogo RBAC). */
  esRolVendedor(): boolean {
    if (esNombreRolVendedor(this.rolNombreWritable())) return true;
    return false;
  }

  /**
   * Refresca nombre de rol y datos de usuario desde `GET /auth/me`.
   * Los permisos del menú **solo** vienen de `POST /auth/login` (no se sobrescriben aquí).
   */
  aplicarPerfilDesdeMe(me: SesionAuthMe | null, _permisos?: PermisoSesion[] | null): void {
    if (!me) return;
    const recordarme = resolverBucketSesionActiva() === 'localStorage';
    if (me.rol_id != null && Number.isFinite(me.rol_id)) {
      const rid = Math.trunc(me.rol_id);
      this.persistRolId(rid, recordarme);
      this.rolIdWritable.set(rid);
    }
    const nombreRol = me.rol?.nombre?.trim() || null;
    if (nombreRol) {
      this.persistRolNombre(nombreRol, recordarme);
      this.rolNombreWritable.set(nombreRol);
    }
    if (me.id != null && Number.isFinite(me.id)) {
      const uid = Math.trunc(me.id);
      this.persistUserId(uid, recordarme);
      this.userIdWritable.set(uid);
    }
  }

  /** Sincroniza perfil al abrir la app (rol y usuario; permisos permanecen los del login). */
  refrescarPerfilSesion(): Observable<SesionAuthMe | null> {
    return this.fetchAuthMe().pipe(
      tap(({ me }) => this.aplicarPerfilDesdeMe(me)),
      map(({ me }) => me),
    );
  }

  private rolAlmacenadoOJwt(): number | null {
    const persisted = this.rolIdWritable();
    if (persisted != null && Number.isFinite(persisted)) return persisted;
    return leerRolIdDesdeJwt(this.getToken());
  }

  /**
   * Puede consultar `GET /organizaciones/perfil` según RBAC (evita prefetch en el shell sin permiso).
   */
  puedeConsultarPerfilOrganizacion(): boolean {
    return (
      this.tienePermiso('ORGANIZACION', 'VER') || this.tienePermiso('ORGANIZACION', 'EDITAR')
    );
  }

  /** Estado de instalación inicial (WordPress-style). Sin JWT. */
  fetchSetupRequired(): Observable<SetupRequiredResponse> {
    return this.http.get<SetupRequiredResponse>(`${this.apiBase}/auth/setup-required`);
  }

  /** Primer administrador; solo cuando `setupRequired === true`. Sin JWT. */
  initialSetup(body: InitialSetupBody): Observable<unknown> {
    return this.http.post(`${this.apiBase}/auth/setup`, body);
  }

  /** Estado de sesión: usuario actual, rol embebido y organización si el API lo envía. */
  fetchAuthMe(): Observable<{ me: SesionAuthMe | null; permisos: PermisoSesion[] }> {
    return this.http.get<unknown>(`${this.apiBase}/auth/me`).pipe(map(parseAuthMeResponse));
  }

  static mapMeErr(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const b = err.error as { msg?: string; message?: string } | string | null;
      if (typeof b === 'string' && b.trim()) return b;
      if (b && typeof b === 'object' && typeof b.msg === 'string') return b.msg;
      if (b && typeof b === 'object' && typeof (b as { message?: string }).message === 'string') {
        return (b as { message: string }).message;
      }
      if (err.status === 401) return 'Sesión caducada o inválida.';
    }
    return 'No pudimos cargar tu perfil. Intentá cerrar sesión e ingresar de nuevo.';
  }

  login(email: string, password: string, recordarme = false): Observable<LoginResponse> {
    return this.http
      .post<unknown>(`${this.apiBase}/auth/login`, {
        email: email.trim(),
        password,
      })
      .pipe(
        map((raw) => parseLoginResponseBody(raw)),
        tap((res) => {
          if (!res) return;
          const permisos = normalizarPermisosLogin(res.permisos);
          const bucket = iniciarBucketSesion(recordarme);
          this.escribirSesionCompleta(bucket, {
            token: res.token,
            permisos,
            nombre: res.usuario?.nombre_completo ?? null,
            rolId:
              res.usuario?.rol_id != null && Number.isFinite(res.usuario.rol_id)
                ? Math.trunc(res.usuario.rol_id)
                : leerRolIdDesdeJwt(res.token),
            userId:
              res.usuario?.id != null && Number.isFinite(res.usuario.id)
                ? Math.trunc(res.usuario.id)
                : null,
            organizacion: res.organizacion ?? null,
          });
          this.nombreWritable.set(res.usuario?.nombre_completo ?? null);
          this.permisosWritable.set(permisos);
          this.rolNombreWritable.set(null);
          this.rolIdWritable.set(
            res.usuario?.rol_id != null && Number.isFinite(res.usuario.rol_id)
              ? Math.trunc(res.usuario.rol_id)
              : leerRolIdDesdeJwt(res.token),
          );
          this.userIdWritable.set(
            res.usuario?.id != null && Number.isFinite(res.usuario.id)
              ? Math.trunc(res.usuario.id)
              : null,
          );
        }),
        map((res) => {
          if (!res) {
            throw new Error('Respuesta de login inválida.');
          }
          return res;
        }),
      );
  }

  private escribirSesionCompleta(
    bucket: AuthSessionBucket,
    data: {
      token: string;
      permisos: PermisoSesion[];
      nombre: string | null;
      rolId: number | null;
      userId: number | null;
      organizacion?: OrganizacionPerfil | null;
    },
  ): void {
    escribirItemEnBucket(bucket, STORAGE_KEY, data.token);
    escribirItemEnBucket(bucket, STORAGE_PERMISOS_KEY, JSON.stringify(data.permisos));
    if (data.nombre?.trim()) {
      escribirItemEnBucket(bucket, STORAGE_NOMBRE_KEY, data.nombre.trim());
    }
    if (data.rolId != null && Number.isFinite(data.rolId)) {
      escribirItemEnBucket(bucket, STORAGE_ROL_ID_KEY, String(Math.trunc(data.rolId)));
    }
    if (data.userId != null && Number.isFinite(data.userId)) {
      escribirItemEnBucket(bucket, STORAGE_USER_ID_KEY, String(Math.trunc(data.userId)));
    }
    if (data.organizacion && Object.keys(data.organizacion).length) {
      escribirItemEnBucket(bucket, STORAGE_ORGANIZACION_KEY, JSON.stringify(data.organizacion));
    }
    this.tokenWritable.set(data.token);
  }

  private persistToken(token: string, recordarme: boolean): void {
    const bucket = resolverBucketSesionActiva() ?? iniciarBucketSesion(recordarme);
    escribirItemEnBucket(bucket, STORAGE_KEY, token);
    this.tokenWritable.set(token);
  }

  private persistNombre(nombre: string | null, recordarme: boolean): void {
    if (!nombre?.trim()) return;
    const bucket = resolverBucketSesionActiva() ?? iniciarBucketSesion(recordarme);
    escribirItemEnBucket(bucket, STORAGE_NOMBRE_KEY, nombre.trim());
  }

  private persistPermisos(permisos: PermisoSesion[], recordarme: boolean): void {
    const bucket = resolverBucketSesionActiva() ?? iniciarBucketSesion(recordarme);
    escribirItemEnBucket(bucket, STORAGE_PERMISOS_KEY, JSON.stringify(permisos));
  }

  private persistRolId(rolId: number | null, recordarme: boolean): void {
    if (rolId == null || !Number.isFinite(rolId)) return;
    const bucket = resolverBucketSesionActiva() ?? iniciarBucketSesion(recordarme);
    escribirItemEnBucket(bucket, STORAGE_ROL_ID_KEY, String(Math.trunc(rolId)));
  }

  private limpiarRolNombrePersistido(): void {
    sessionStorage.removeItem(STORAGE_ROL_NOMBRE_KEY);
    localStorage.removeItem(STORAGE_ROL_NOMBRE_KEY);
  }

  private persistRolNombre(nombre: string | null, recordarme: boolean): void {
    this.limpiarRolNombrePersistido();
    if (!nombre?.trim()) return;
    const bucket = resolverBucketSesionActiva() ?? iniciarBucketSesion(recordarme);
    escribirItemEnBucket(bucket, STORAGE_ROL_NOMBRE_KEY, nombre.trim());
  }

  private readRolNombreFromStorage(): string | null {
    const raw = leerItemSesionActiva(STORAGE_ROL_NOMBRE_KEY);
    return raw?.trim() || null;
  }

  private persistUserId(userId: number | null, recordarme: boolean): void {
    if (userId == null || !Number.isFinite(userId)) return;
    const bucket = resolverBucketSesionActiva() ?? iniciarBucketSesion(recordarme);
    escribirItemEnBucket(bucket, STORAGE_USER_ID_KEY, String(Math.trunc(userId)));
  }

  private readTokenFromStorage(): string | null {
    return leerItemSesionActiva(STORAGE_KEY);
  }

  private readNombreFromStorage(): string | null {
    return leerItemSesionActiva(STORAGE_NOMBRE_KEY);
  }

  private readRolIdFromStorage(): number | null {
    const raw = leerItemSesionActiva(STORAGE_ROL_ID_KEY);
    if (raw == null || raw === '' || !/^-?\d+$/.test(raw.trim())) return null;
    const n = Number.parseInt(raw.trim(), 10);
    return Number.isFinite(n) ? n : null;
  }

  private readUserIdFromStorage(): number | null {
    const raw = leerItemSesionActiva(STORAGE_USER_ID_KEY);
    if (raw == null || raw === '' || !/^-?\d+$/.test(raw.trim())) return null;
    const n = Number.parseInt(raw.trim(), 10);
    return Number.isFinite(n) ? n : null;
  }

  private readPermisosFromStorage(): PermisoSesion[] {
    const raw = leerItemSesionActiva(STORAGE_PERMISOS_KEY);
    if (!raw?.trim()) return [];
    try {
      const parsed = JSON.parse(raw) as unknown;
      return normalizarPermisosLogin(parsed);
    } catch {
      return [];
    }
  }
}

function parseLoginResponseBody(raw: unknown): LoginResponse | null {
  if (!raw || typeof raw !== 'object') return null;
  const root = raw as Record<string, unknown>;
  const nested = root['data'];
  const body =
    nested && typeof nested === 'object' && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : root;

  const tokenRaw = body['token'] ?? body['access_token'] ?? body['accessToken'];
  if (typeof tokenRaw !== 'string' || !tokenRaw.trim()) return null;

  const usuarioRaw = body['usuario'] ?? body['user'];
  if (!usuarioRaw || typeof usuarioRaw !== 'object') return null;
  const u = usuarioRaw as Record<string, unknown>;

  const id = numeroDe(u, 'id');
  const rolId = numeroDe(u, 'rol_id', 'role_id');
  const nombre = textoDe(u, 'nombre_completo', 'nombreCompleto', 'nombre');
  if (id == null || rolId == null || !nombre) return null;

  const permisosRaw =
    body['permisos'] ??
    body['permissions'] ??
    root['permisos'] ??
    root['permissions'] ??
    u['permisos'] ??
    u['permissions'];

  const orgId = numeroDe(u, 'organizacion_id', 'organization_id');

  const orgRaw = body['organizacion'] ?? body['organization'];
  const organizacionParsed = orgRaw ? normalizarOrganizacionPerfilDesdeApi(orgRaw) : null;
  const organizacion =
    organizacionParsed &&
    Object.values(organizacionParsed).some(
      (v) => v !== null && v !== undefined && String(v).trim() !== '',
    )
      ? organizacionParsed
      : null;

  return {
    token: tokenRaw.trim(),
    usuario: {
      id: Math.trunc(id),
      rol_id: Math.trunc(rolId),
      nombre_completo: nombre,
      ...(orgId != null ? { organizacion_id: Math.trunc(orgId) } : {}),
    },
    permisos: normalizarPermisosLogin(permisosRaw),
    ...(organizacion ? { organizacion } : {}),
  };
}

function parseAuthMeResponse(raw: unknown): { me: SesionAuthMe | null; permisos: PermisoSesion[] } {
  const root = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const permisos = normalizarPermisosLogin(root['permisos'] ?? root['permissions']);
  const me = normalizarSesionAuthMe(raw);
  return { me, permisos };
}

function leerRolIdDesdeJwt(token: string | null): number | null {
  if (!token) return null;
  const d1 = token.indexOf('.');
  if (d1 < 0) return null;
  const d2 = token.indexOf('.', d1 + 1);
  if (d2 <= d1 + 1) return null;
  let b64 = token.slice(d1 + 1, d2).replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4;
  if (pad) b64 += '='.repeat(4 - pad);
  try {
    const json = atob(b64);
    const o = JSON.parse(json) as { rol_id?: unknown };
    const r = o.rol_id;
    if (typeof r === 'number' && Number.isFinite(r)) return Math.trunc(r);
    if (typeof r === 'string' && /^-?\d+$/.test(r.trim())) {
      const n = Number.parseInt(r.trim(), 10);
      return Number.isFinite(n) ? n : null;
    }
  } catch {
    return null;
  }
  return null;
}

function normalizarPermisosLogin(raw: unknown): PermisoSesion[] {
  if (!Array.isArray(raw)) return [];
  const out: PermisoSesion[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    let id: number | null = null;
    const idRaw = o['id'];
    if (typeof idRaw === 'number' && Number.isFinite(idRaw)) id = Math.trunc(idRaw);
    else if (typeof idRaw === 'string' && /^-?\d+$/.test(idRaw.trim())) {
      const n = Number.parseInt(idRaw.trim(), 10);
      if (Number.isFinite(n)) id = n;
    }
    const modulo = textoPermiso(o['modulo'] ?? o['module']);
    const accion = textoPermiso(o['accion'] ?? o['action']);
    const descripcion =
      textoPermiso(o['descripcion'] ?? o['description'] ?? o['detalle']) ?? null;
    if (!modulo || !accion) continue;
    out.push({
      id: id ?? 0,
      modulo,
      accion,
      ...(descripcion ? { descripcion } : {}),
    });
  }
  return out;
}

function textoPermiso(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s : null;
}

function numeroDe(obj: Record<string, unknown>, ...claves: string[]): number | null {
  for (const k of claves) {
    const v = obj[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && /^-?\d+$/.test(v.trim())) {
      const n = Number.parseInt(v.trim(), 10);
      return Number.isFinite(n) ? n : null;
    }
  }
  return null;
}

function textoDe(obj: Record<string, unknown>, ...claves: string[]): string | null {
  for (const k of claves) {
    const v = obj[k];
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return null;
}

function parseRolEmbebido(f: Record<string, unknown>): RolResumido | null {
  const ro = f['rol'] ?? f['role'];
  if (!ro || typeof ro !== 'object') return null;
  const r = ro as Record<string, unknown>;
  const id = numeroDe(r, 'id');
  const nombre = textoDe(r, 'nombre', 'name');
  if (id == null || !nombre) return null;
  return {
    id: Math.trunc(id),
    nombre,
    descripcion: textoDe(r, 'descripcion', 'description'),
  };
}

/**
 * Tolera usuario plano en la raíz o bajo `usuario`, y rol anidado o plano junto al usuario.
 */
export function normalizarSesionAuthMe(raw: unknown): SesionAuthMe | null {
  if (!raw || typeof raw !== 'object') return null;
  const root = raw as Record<string, unknown>;
  const usuarioLike = root['usuario'];
  const base: Record<string, unknown> =
    usuarioLike && typeof usuarioLike === 'object' ? (usuarioLike as Record<string, unknown>) : root;

  const id = numeroDe(base, 'id', 'usuario_id');
  const nombreCompleto = textoDe(base, 'nombre_completo', 'nombreCompleto', 'nombre');
  let rolId = numeroDe(base, 'rol_id', 'role_id');
  let rol = parseRolEmbebido(base);
  if (!rol && (root['rol'] ?? root['role'])) {
    rol = parseRolEmbebido(root);
    if (rol && rolId == null) rolId = rol.id;
  }
  if (rol && rolId == null) rolId = rol.id;
  const email = textoDe(base, 'email');
  const orgId = numeroDe(base, 'organizacion_id', 'organization_id');
  if (id == null || !nombreCompleto || rolId == null) return null;
  return {
    id: Math.trunc(id),
    nombre_completo: nombreCompleto,
    email: email ?? null,
    rol_id: Math.trunc(rolId),
    rol,
    ...(orgId != null ? { organizacion_id: Math.trunc(orgId) } : {}),
  };
}
